import CartEmpty from './CartEmpty';
import CartItem from './CartItem';
import CartTotals from './CartTotals';
import CartUpsell from './CartUpsell';
import cn from 'classnames';
import { formatMoney } from '@shopify/theme-currency';
import { getTranslation } from '../helpers/utilities';
import { h } from 'preact';
import { useCartContext } from '../context/CartContext';

import { createPortal, useEffect, useState } from 'preact/compat';

const MiniCartDrawerEl = document.getElementById('preact-mini-cart-drawer');

const MiniCart = () => {
  const { cart, toggleMiniCart, isMiniCartOpen, miniCartData } =
    useCartContext();
  const [threshold, setThreshold] = useState(0);

  useEffect(() => {
    const originalThresholds = miniCartData.settings.freeShippingThreshold;
    const cartCurrency = miniCartData.settings.cartCurrency;
    if (cartCurrency === 'USD') {
      const threesholdUSD = originalThresholds
        .split('|')[0]
        .replace('USD:', '');
      setThreshold(threesholdUSD);
    } else if (cartCurrency === 'CAD') {
      const thresholdCAD = originalThresholds.split('|')[1].replace('CAD:', '');
      setThreshold(thresholdCAD);
    }
  }, [cart, miniCartData]);

  // Prevents redirect to cart page when donation is checked / updated.
  useEffect(() => {
    window.dkRefreshCartAction = () => {};
  }, []);

  let percentage = 0;

  const thresholdInCents = threshold * 100;
  const difference = thresholdInCents - cart.total_price;

  if (difference > 0) {
    percentage = (cart.total_price * 100) / thresholdInCents;
  } else {
    percentage = 100;
  }

  return createPortal(
    <div
      class={cn(
        'fixed inset-0',
        isMiniCartOpen ? 'opacity-1 visible' : `invisible opacity-0 delay-300`,
      )}
    >
      <div
        onClick={() => toggleMiniCart(false)}
        class={cn(
          `absolute inset-0 bg-nightfall transition-opacity duration-300`,
          isMiniCartOpen ? 'opacity-40' : 'opacity-0',
        )}
      />
      <div
        class={cn(
          `absolute top-0 bottom-0 right-0 z-50 flex w-11/12 flex-col rounded-tl-4xl rounded-br-4xl bg-sand px-4 pb-4 transition-transform duration-300 sm:w-full sm:max-w-md md:top-4 md:bottom-4 md:right-4 md:max-w-xl md:px-8`,
          isMiniCartOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div class="pt-4 pb-6 text-center md:py-6">
          <div class="mb-2 flex items-center justify-between">
            <div></div>
            {threshold > 0 && (
              <div class="type-small">
                {percentage === 100
                  ? getTranslation('mini_cart.freeShippingCompleteMessage')
                  : getTranslation('mini_cart.freeShippingMessage', {
                      amount: formatMoney(difference, theme.moneyFormat),
                    })}
              </div>
            )}

            <button onClick={() => toggleMiniCart(false)}>
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {threshold > 0 && (
            <div class="h-2 w-full rounded-full bg-clay">
              <div
                class="h-2 rounded-full bg-coral"
                style={{ width: `${percentage.toFixed(2)}%` }}
              ></div>
            </div>
          )}
        </div>

        <div
          class="hide-scrollbar data-scroll-lock-scrollable overflow-auto"
          id="cart-items"
          data-scroll-lock-scrollable
        >
          {cart.items && cart.items.length > 0 ? (
            cart.items.map(item => <CartItem item={item} key={item.id} />)
          ) : (
            <CartEmpty />
          )}

          <div
            class={cn('dk-flat-static', { hidden: cart.items.length === 0 })}
          ></div>

          {cart.items &&
          cart.items.length > 0 &&
          miniCartData.settings.showUpsells ? (
            <CartUpsell cart={{ ...cart }} />
          ) : null}
        </div>

        {cart.items && cart.items.length > 0 ? (
          <div class="-mx-4 mt-auto border-t border-t-slate px-4 pt-4 pb-2 md:-mx-8 md:px-8 md:pt-6 md:pb-3">
            {cart.total_price > 0 && <CartTotals className="type-body-2" />}
            <div class="type-body-2 mt-2 flex justify-between">
              <p class="text-stone">{getTranslation('mini_cart.shipping')}</p>
              <p>{getTranslation('mini_cart.shipping_at_checkout')}</p>
            </div>

            <div class="mt-6">
              <form action={`${Shopify.routes.root}cart`} method="POST">
                <button
                  type="submit"
                  name="checkout"
                  class="button button--primary w-full"
                >
                  {getTranslation('mini_cart.checkout')}{' '}
                  {formatMoney(cart.total_price, theme.moneyFormat)}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    MiniCartDrawerEl,
  );
};

export default MiniCart;
